import { STORAGE_KEYS } from './storageKeys';

/**
 * DineOps multi-outlet: the outlet this browser tab works in.
 *
 * The active outlet is per TAB (sessionStorage), so switching outlet in one tab
 * never retargets the kitchen display, Settle dialog or Bill Queue open in
 * another. localStorage keeps the last choice only so a NEW tab starts in it:
 * the first read in a tab seeds sessionStorage from localStorage. A switch
 * writes both.
 *
 * Every read of the outlet (the X-Outlet-ID header, print scopes, screen
 * labels) must go through these helpers — never read the storage keys directly.
 */

const read = (store: Storage, key: string): string | null => {
  try { return store.getItem(key); } catch { return null; }
};
const write = (store: Storage, key: string, value: string) => {
  try { store.setItem(key, value); } catch { /* storage disabled: non-fatal */ }
};
const remove = (store: Storage, key: string) => {
  try { store.removeItem(key); } catch { /* storage disabled: non-fatal */ }
};

/** First read in a tab: copy the last choice (localStorage) into this tab. */
const seedTab = () => {
  if (read(sessionStorage, STORAGE_KEYS.DINE_OUTLET_ID)) return;
  const id = read(localStorage, STORAGE_KEYS.DINE_OUTLET_ID);
  if (!id) return;
  write(sessionStorage, STORAGE_KEYS.DINE_OUTLET_ID, id);
  const name = read(localStorage, STORAGE_KEYS.DINE_OUTLET_NAME);
  if (name) write(sessionStorage, STORAGE_KEYS.DINE_OUTLET_NAME, name);
};

/** This tab's outlet id; '' when none. */
export const getActiveDineOutletId = (): string => {
  seedTab();
  return read(sessionStorage, STORAGE_KEYS.DINE_OUTLET_ID) ?? '';
};

/** This tab's outlet name; '' when none. */
export const getActiveDineOutletName = (): string => {
  seedTab();
  return read(sessionStorage, STORAGE_KEYS.DINE_OUTLET_NAME) ?? '';
};

/** Switch this tab's outlet and remember it as the last choice for new tabs. */
export const setActiveDineOutlet = (id: string, name: string) => {
  write(sessionStorage, STORAGE_KEYS.DINE_OUTLET_ID, id);
  write(sessionStorage, STORAGE_KEYS.DINE_OUTLET_NAME, name);
  write(localStorage, STORAGE_KEYS.DINE_OUTLET_ID, id);
  write(localStorage, STORAGE_KEYS.DINE_OUTLET_NAME, name);
};

/** No outlet (the user has none): clear this tab and the last choice. */
export const clearActiveDineOutlet = () => {
  remove(sessionStorage, STORAGE_KEYS.DINE_OUTLET_ID);
  remove(sessionStorage, STORAGE_KEYS.DINE_OUTLET_NAME);
  remove(localStorage, STORAGE_KEYS.DINE_OUTLET_ID);
  remove(localStorage, STORAGE_KEYS.DINE_OUTLET_NAME);
};

/**
 * Screens that work in one outlet: the Restaurant (dineops-*) and Printing
 * (print-*) screens. An outlet switch closes their parked forms and remounts
 * them so they re-read the outlet. Tenant masters and reports that never read the
 * header outlet are left alone (Kitchens, Requisition Register, 2026-10-22).
 * Requisitions stays outlet-scoped: its list starts at the working outlet, and
 * Send and Issue act in it.
 */
const OUTLET_INDEPENDENT_MODULES = new Set(['dineops-kitchens', 'dineops-requisition-report', 'dineops-approval-rules', 'dineops-price-board']);

export const isOutletScopedModule = (moduleId: string): boolean =>
  !OUTLET_INDEPENDENT_MODULES.has(moduleId) && (moduleId.startsWith('dineops-') || moduleId.startsWith('print-'));
